// Reading values that SHOULD be a given shape but came from somewhere that cannot promise it: a stored
// setting, an imported card, a model's JSON, a chat blob written by an older build. Every one of these
// answers "give me this shape, drop whatever isn't" rather than throwing, because the callers are load
// paths where a single bad element must not take the whole card, chat or settings row down with it.
//
// Written out rather than reached for from a library so the exact tolerance is visible and testable:
// what counts, what is dropped, and what a non-array or a null becomes.

/**
 * Every string in a value that should be an array of them.
 *
 * A non-array (missing, null, an object, a bare string) is an empty array, NOT a one-element one: these
 * read fields like `tags` and `greetings`, where a malformed value means "none recorded", and quietly
 * turning a stray string into a single entry would invent data that was never stored.
 */
export const strArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

/** As `strArray`, with blank and whitespace-only entries dropped and the rest trimmed. */
export const trimmedStrArray = (v: unknown): string[] =>
  strArray(v).map((s) => s.trim()).filter(Boolean);

/** A string, or "" for anything that is not one. `null` and `undefined` included, so callers can treat
 *  "absent" and "empty" the same way — which every text field in this app does. */
export const str = (v: unknown): string => (typeof v === "string" ? v : "");

/** A finite number, or `undefined`. NaN and Infinity are not numbers worth storing: they survive
 *  `typeof === "number"`, reach the UI, and render as "NaN" in a field the user then cannot fix. */
export const num = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

/**
 * A plain object's own entries, or `{}`.
 *
 * Arrays are rejected on purpose even though `typeof [] === "object"`: a caller asking for a map and
 * getting an array would read its indices as keys, which is how a stored list turns into `{ "0": … }`.
 */
export const plainObject = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/** A map of name to number, dropping any entry whose value is not a finite number. */
export const numMap = (v: unknown): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [k, x] of Object.entries(plainObject(v))) {
    const n = num(x);
    if (n !== undefined) out[k] = n;
  }
  return out;
};

/** `JSON.parse` that answers `undefined` instead of throwing. For stored text that a user, an older
 *  build or a half-finished write may have left unparseable. */
export function parseJson(text: string | null | undefined): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/**
 * A thrown value as text fit for a UI.
 *
 * `catch (e)` hands over `unknown`: an `Error`, a bare string (what a rejected Tauri command sends), or
 * anything else. `String(e)` covers all three, and the strip removes the `Error: ` that `toString` puts in
 * front of a message the user is about to read as a sentence. An `Error`'s own `message` is preferred over
 * its `toString`, which would keep a subclass name (`TypeError: `) that means nothing to a reader.
 */
export const errText = (e: unknown): string =>
  (e instanceof Error ? e.message : String(e)).replace(/^Error:\s*/, "");
